import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Calendar, Briefcase } from 'lucide-react';

const ClientConfiguration = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contractTerms, setContractTerms] = useState(['1yr', '2yr', '3yr', '4yr', '5yr']);
  const [newTerm, setNewTerm] = useState('');
  const [jobTitles, setJobTitles] = useState(['staff', 'team_leader', 'manager', 'director']);
  const [newJobTitle, setNewJobTitle] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setContractTerms(response.data.contract_terms || ['1yr', '2yr', '3yr', '4yr', '5yr']);
      setJobTitles(response.data.job_titles || ['staff', 'team_leader', 'manager', 'director']);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTerm = () => {
    if (!newTerm.trim()) {
      toast.error('Please enter a term value');
      return;
    }
    
    const termPattern = /^\d+yr$/;
    if (!termPattern.test(newTerm.trim())) {
      toast.error('Term must be in format like "1yr", "6yr", etc.');
      return;
    }

    if (contractTerms.includes(newTerm.trim())) {
      toast.error('Term already exists');
      return;
    }

    setContractTerms([...contractTerms, newTerm.trim()]);
    setNewTerm('');
    toast.success('Term added. Click "Save Contract Terms" to persist.');
  };

  const handleRemoveTerm = (term) => {
    setContractTerms(contractTerms.filter(t => t !== term));
    toast.success('Term removed. Click "Save Contract Terms" to persist.');
  };

  const handleSaveContractTerms = async () => {
    setSaving(true);
    try {
      await api.put('/settings', {
        contract_terms: contractTerms
      });
      toast.success('Contract terms saved successfully!');
    } catch (error) {
      toast.error('Failed to save contract terms');
    } finally {
      setSaving(false);
    }
  };

  const handleAddJobTitle = () => {
    if (!newJobTitle.trim()) {
      toast.error('Please enter a job title');
      return;
    }

    const formattedTitle = newJobTitle.trim().toLowerCase().replace(/\s+/g, '_');
    
    if (jobTitles.includes(formattedTitle)) {
      toast.error('Job title already exists');
      return;
    }

    setJobTitles([...jobTitles, formattedTitle]);
    setNewJobTitle('');
    toast.success('Job title added. Click "Save Job Titles" to persist.');
  };

  const handleRemoveJobTitle = (title) => {
    const defaults = ['staff', 'team_leader', 'manager', 'director'];
    if (defaults.includes(title)) {
      toast.error('Cannot remove default job titles');
      return;
    }
    
    setJobTitles(jobTitles.filter(t => t !== title));
    toast.success('Job title removed. Click "Save Job Titles" to persist.');
  };

  const handleSaveJobTitles = async () => {
    setSaving(true);
    try {
      await api.put('/settings', {
        job_titles: jobTitles
      });
      toast.success('Job titles saved successfully!');
    } catch (error) {
      toast.error('Failed to save job titles');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/settings')}
              className="border-slate-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Client Configuration</h1>
                <p className="text-sm text-slate-500">Manage contract terms and job titles</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Contract Terms */}
        <Card className="shadow-sm border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              <span>Contract Terms</span>
            </CardTitle>
            <CardDescription>
              Define available contract term lengths for client agreements
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Current Terms</Label>
                <div className="flex flex-wrap gap-2">
                  {contractTerms && contractTerms.length > 0 ? (
                    contractTerms.map((term, index) => (
                      <div key={index} className="flex items-center">
                        <Badge variant="outline" className="px-3 py-1 text-sm">
                          {term}
                          <button
                            onClick={() => handleRemoveTerm(term)}
                            className="ml-2 text-red-500 hover:text-red-700"
                            type="button"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-400 text-sm">No contract terms defined</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-term">Add New Term</Label>
                <div className="flex gap-2">
                  <Input
                    id="new-term"
                    type="text"
                    placeholder="e.g., 6yr"
                    value={newTerm}
                    onChange={(e) => setNewTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddTerm()}
                    className="border-slate-300"
                  />
                  <Button
                    type="button"
                    onClick={handleAddTerm}
                    variant="outline"
                    className="border-purple-300 text-purple-600 hover:bg-purple-50"
                  >
                    Add
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Format: Number followed by 'yr' (e.g., "1yr", "6yr")
                </p>
              </div>

              <Button
                onClick={handleSaveContractTerms}
                disabled={saving}
                className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-md"
              >
                {saving ? 'Saving...' : 'Save Contract Terms'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Job Titles */}
        <Card className="shadow-sm border-slate-200 mt-6">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Briefcase className="w-5 h-5 text-indigo-600" />
              <span>Job Titles</span>
            </CardTitle>
            <CardDescription>
              Manage available job titles for client contacts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Current Job Titles</Label>
                <div className="flex flex-wrap gap-2">
                  {jobTitles && jobTitles.length > 0 ? (
                    jobTitles.map((title, index) => {
                      const isDefault = ['staff', 'team_leader', 'manager', 'director'].includes(title);
                      return (
                        <div key={index} className="flex items-center">
                          <Badge 
                            variant="outline" 
                            className={`px-3 py-1 text-sm ${
                              isDefault ? 'bg-indigo-50 border-indigo-300' : ''
                            }`}
                          >
                            {title.replace(/_/g, ' ')}
                            {!isDefault && (
                              <button
                                onClick={() => handleRemoveJobTitle(title)}
                                className="ml-2 text-red-500 hover:text-red-700"
                                type="button"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </Badge>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-slate-400 text-sm">No job titles defined</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-job-title">Add New Job Title</Label>
                <div className="flex gap-2">
                  <Input
                    id="new-job-title"
                    type="text"
                    placeholder="e.g., Senior Manager"
                    value={newJobTitle}
                    onChange={(e) => setNewJobTitle(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddJobTitle()}
                    className="border-slate-300"
                  />
                  <Button
                    type="button"
                    onClick={handleAddJobTitle}
                    variant="outline"
                    className="border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                  >
                    Add
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Will be converted to lowercase with underscores (e.g., "Senior Manager" → "senior_manager")
                </p>
              </div>

              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <h4 className="font-semibold text-indigo-900 mb-2 text-sm">Default Job Titles (cannot be removed):</h4>
                <ul className="text-xs text-indigo-800 space-y-1 list-disc list-inside">
                  <li><strong>Staff</strong> - Standard employee</li>
                  <li><strong>Team Leader</strong> - Leads a team, may approve requests</li>
                  <li><strong>Manager</strong> - Department manager, approves requests</li>
                  <li><strong>Director</strong> - Senior leadership, full approval rights</li>
                </ul>
              </div>

              <Button
                onClick={handleSaveJobTitles}
                disabled={saving}
                className="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 shadow-md"
              >
                {saving ? 'Saving...' : 'Save Job Titles'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ClientConfiguration;
